import React, { memo, useMemo } from 'react';
import { FaBox, FaMoneyBillWave, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import { Product } from '../../types/product';
import { AdminTable } from '../admin/AdminTable';
import { ProductListHeader } from './ProductManagementComponents';

interface ProductTableProps {
  products: Product[];
  onEdit: (product: Product) => void;
  onConfirmDelete: (product: Product) => void;
  getMainCategoryName: (id: number | string) => string;
  getSubCategoryName: (id: number | string) => string;
}

export const ProductTable = memo<ProductTableProps>(
  ({ products, onEdit, onConfirmDelete, getMainCategoryName, getSubCategoryName }) => {
    const columns = useMemo(
      () => [
        {
          header: 'ID',
          accessor: (product: Product) => <ProductIdCell product={product} />,
        },
        {
          header: 'Ürün',
          accessor: (product: Product) => <ProductInfoCell product={product} />,
        },
        {
          header: 'Fiyat',
          accessor: (product: Product) => <ProductPriceCell product={product} />,
        },
        {
          header: 'Kategori',
          accessor: (product: Product) => (
            <ProductCategoryCell
              product={product}
              getMainCategoryName={getMainCategoryName}
              getSubCategoryName={getSubCategoryName}
            />
          ),
        },
        {
          header: 'Seçenekli',
          accessor: (product: Product) => <ProductOptionsCell product={product} />,
        },
      ],
      [getMainCategoryName, getSubCategoryName]
    );

    return (
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
        <ProductListHeader count={products.length} />

        <div className="overflow-x-auto">
          <AdminTable
            columns={columns}
            data={products}
            onEdit={onEdit}
            onConfirmDelete={onConfirmDelete}
          />
        </div>
      </div>
    );
  }
);

ProductTable.displayName = 'ProductTable';

// Individual cell components for better performance and maintainability
const ProductIdCell = memo<{ product: Product }>(({ product }) => (
  <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded-md">#{product.id}</span>
));

ProductIdCell.displayName = 'ProductIdCell';

const ProductInfoCell = memo<{ product: Product }>(({ product }) => (
  <div className="flex items-center gap-3">
    <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
      <FaBox className="text-white text-sm" />
    </div>
    <div>
      <p className="font-semibold text-gray-900">{product.name}</p>
      <p className="text-sm text-gray-500 truncate max-w-xs">{product.description}</p>
    </div>
  </div>
));

ProductInfoCell.displayName = 'ProductInfoCell';

const ProductPriceCell = memo<{ product: Product }>(({ product }) => {
  const price = typeof product.price === 'string' ? parseFloat(product.price) : product.price;
  const displayPrice = isNaN(price) ? '0.00' : price.toFixed(2);

  return (
    <div className="flex items-center gap-2">
      <FaMoneyBillWave className="text-green-500" />
      <span className="font-bold text-green-600">{displayPrice} ₺</span>
    </div>
  );
});

ProductPriceCell.displayName = 'ProductPriceCell';

interface ProductCategoryCellProps {
  product: Product;
  getMainCategoryName: (id: number | string) => string;
  getSubCategoryName: (id: number | string) => string;
}

const ProductCategoryCell = memo<ProductCategoryCellProps>(
  ({ product, getMainCategoryName, getSubCategoryName }) => (
    <div className="space-y-1">
      <div className="flex items-center gap-1">
        <svg className="w-3 h-3 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z"
            clipRule="evenodd"
          />
        </svg>
        <span className="text-sm font-medium text-blue-600">
          {getMainCategoryName(product.main_category_id)}
        </span>
      </div>
      <div className="text-xs text-gray-500">{getSubCategoryName(product.category_id)}</div>
    </div>
  )
);

ProductCategoryCell.displayName = 'ProductCategoryCell';

const ProductOptionsCell = memo<{ product: Product }>(({ product }) => (
  <div className="flex items-center gap-2">
    {product.has_options ? (
      <>
        <FaToggleOn className="text-green-500 text-lg" />
        <span className="text-green-600 font-medium">Evet</span>
      </>
    ) : (
      <>
        <FaToggleOff className="text-gray-400 text-lg" />
        <span className="text-gray-500">Hayır</span>
      </>
    )}
  </div>
));

ProductOptionsCell.displayName = 'ProductOptionsCell';
